import type { TFunction } from 'i18next'
import type { FieldErrors } from 'react-hook-form'
import {
  AboutTab,
  VoicesTab,
  PersonalityTab,
  InstructionsTab,
  FAQTab,
  TelephonyTab,
  IntegrationsTab,
  NotificationsTab,
  AdminSettingsTab,
  RevisionsTab,
} from '../components/tabs'

/**
 * Tab identifiers for the assistant form pages.
 * 'revisions' is only available in edit mode.
 */
export type TabId =
  | 'about'
  | 'voices'
  | 'personality'
  | 'instructions'
  | 'faq'
  | 'telephony'
  | 'integrations'
  | 'notifications'
  | 'admin'
  | 'revisions'

/**
 * Configuration for a single tab in the assistant form.
 */
export interface TabConfig {
  id: TabId
  label: string
  component: React.ComponentType
  /** Only show for admin/partner users */
  adminOnly?: boolean
  /** Only show in edit mode (not create mode) */
  editOnly?: boolean
  /** Optional badge text shown next to the tab label (e.g. "Beta") */
  badge?: string
}

/**
 * Mapping of tab IDs to form field names for error indication.
 * When a field has an error, the corresponding tab will show an error indicator.
 */
export const TAB_FIELD_MAPPING: Record<TabId, string[]> = {
  about: ['name', 'customer_id', 'timezone', 'prompt_scenarios', 'config'],
  voices: ['voice_id'],
  personality: ['greeting', 'default_language', 'additional_languages', 'lexicon'],
  instructions: ['prompt', 'post_call_prompt'],
  faq: [],
  telephony: ['phone_number_id', 'fallback_number'],
  integrations: [],
  notifications: ['notification_user', 'notification_outcomes'],
  admin: ['ai_model', 'ai_deployment_id'],
  revisions: [],
}

/**
 * Creates the tab configuration array with translated labels.
 *
 * @param t - The translation function from react-i18next
 * @returns Array of tab configurations
 */
export function getAssistantTabs(t: TFunction): TabConfig[] {
  return [
    { id: 'about', label: t('resources.assistants.tabs.business_information'), component: AboutTab },
    { id: 'personality', label: t('resources.assistants.tabs.personality'), component: PersonalityTab },
    { id: 'voices', label: t('resources.assistants.tabs.voices'), component: VoicesTab },
    { id: 'instructions', label: t('resources.assistants.tabs.tasks'), component: InstructionsTab },
    { id: 'faq', label: t('resources.assistants.tabs.policies_and_context'), component: FAQTab },
    { id: 'telephony', label: t('resources.assistants.tabs.telephony'), component: TelephonyTab },
    { id: 'integrations', label: t('resources.assistants.tabs.integrations'), component: IntegrationsTab },
    { id: 'notifications', label: t('resources.assistants.tabs.notifications'), component: NotificationsTab },
    { id: 'admin', label: t('resources.assistants.tabs.admin'), component: AdminSettingsTab, adminOnly: true },
    { id: 'revisions', label: t('resources.assistants.tabs.revisions'), component: RevisionsTab, editOnly: true },
  ]
}

/**
 * Checks if a tab has validation errors based on its associated form fields.
 *
 * @param tabId - The tab identifier
 * @param errors - The form errors object from react-hook-form
 * @returns True if any field in the tab has an error
 */
export function tabHasErrors(tabId: TabId, errors: FieldErrors): boolean {
  const fields = TAB_FIELD_MAPPING[tabId]
  return fields.some((field) => field in errors)
}

/**
 * Filters tabs based on user permissions and mode.
 *
 * @param tabs - Array of tab configurations
 * @param options - Filter options
 * @returns Filtered array of visible tabs
 */
export function getVisibleTabs(
  tabs: TabConfig[],
  options: { isAdminOrPartner: boolean; isEditMode: boolean }
): TabConfig[] {
  return tabs.filter((tab) => {
    if (tab.adminOnly && !options.isAdminOrPartner) return false
    if (tab.editOnly && !options.isEditMode) return false
    return true
  })
}

/**
 * Lexicon entry type for pronunciation customization.
 */
interface LexiconEntry {
  original: string
  replacement: string
  type: 'replacement' | 'phonetic'
}

/**
 * Default form values for creating a new assistant.
 */
export const DEFAULT_FORM_VALUES = {
  name: '',
  greeting: '',
  prompt: '',
  additional_languages: [] as string[],
  notification_outcomes: [] as string[],
  config: {} as Record<string, unknown>,
  lexicon: {} as Record<string, LexiconEntry[]>,
  prompt_scenarios: {} as Record<string, string | undefined>,
}
